import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../context/LanguageContext';

// ---------- animations ----------
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 rgba(156, 107, 255, 0.0); }
  50%      { box-shadow: 0 0 28px rgba(156, 107, 255, 0.35); }
`;

// ---------- styled ----------
const FormContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-7) var(--space-4);
  animation: ${fadeUp} 0.7s var(--ease-soft);
`;

const Eyebrow = styled.span`
  display: inline-block;
  font-family: var(--font-display);
  letter-spacing: 0.32em;
  text-transform: uppercase;
  font-size: 0.78rem;
  color: var(--color-gold);
  margin-bottom: var(--space-3);
  opacity: 0.9;
`;

const Title = styled.h1`
  text-align: center;
  font-family: var(--font-display);
  color: var(--color-gold-soft);
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 600;
  margin: 0 0 var(--space-3);
  text-shadow: 0 0 28px rgba(156, 107, 255, 0.35);
`;

const Subtitle = styled.p`
  text-align: center;
  color: var(--color-text-secondary);
  max-width: 520px;
  margin: 0 auto var(--space-6);
  line-height: var(--lh-normal);
`;

const Form = styled.form`
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  padding: var(--space-6);
  border-radius: var(--radius-lg);
  background:
    linear-gradient(180deg, rgba(21, 26, 51, 0.85) 0%, rgba(11, 16, 32, 0.92) 100%);
  border: 1px solid var(--color-border-strong);
  box-shadow: var(--shadow-elevated), inset 0 1px 0 rgba(255, 255, 255, 0.04);
  position: relative;

  /* gold inner ring */
  &::before {
    content: '';
    position: absolute;
    inset: 6px;
    border-radius: calc(var(--radius-lg) - 6px);
    border: 1px solid var(--color-border-gold);
    pointer-events: none;
    opacity: 0.4;
  }

  @media (max-width: 480px) {
    padding: var(--space-5);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: var(--space-4) var(--space-4);
  border-radius: var(--radius-md);
  background: rgba(7, 9, 26, 0.7);
  border: 1px solid var(--color-border-strong);
  color: var(--color-text-primary);
  font-family: var(--font-body);
  font-size: 1rem;
  line-height: var(--lh-normal);
  min-height: 120px;
  resize: vertical;
  transition: border-color var(--dur-base) var(--ease-soft),
              box-shadow var(--dur-base) var(--ease-soft),
              background var(--dur-base) var(--ease-soft);

  &::placeholder {
    color: var(--color-text-muted);
    font-style: italic;
  }

  &:focus {
    outline: none;
    border-color: var(--color-accent-violet);
    background: rgba(11, 16, 32, 0.85);
    box-shadow: var(--glow-violet);
  }
`;

const Button = styled.button`
  align-self: center;
  padding: 14px 36px;
  background: linear-gradient(135deg, var(--color-accent-purple), var(--color-accent-violet));
  color: var(--color-text-primary);
  border: 1px solid rgba(246, 231, 193, 0.35);
  border-radius: var(--radius-pill);
  font-family: var(--font-display);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform var(--dur-base) var(--ease-soft),
              box-shadow var(--dur-base) var(--ease-soft),
              filter var(--dur-base) var(--ease-soft);
  box-shadow: 0 6px 20px rgba(110, 76, 207, 0.45);
  animation: ${glowPulse} 4.5s ease-in-out infinite;

  &:hover {
    transform: translateY(-2px);
    filter: brightness(1.08);
    box-shadow: var(--glow-violet-strong);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid var(--color-gold);
    outline-offset: 3px;
  }
`;

const QuestionForm = () => {
  const [question, setQuestion] = useState('');
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalQuestion = question.trim() || t('questionForm.defaultQuestion');
    navigate('/cards', { state: { question: finalQuestion } });
  };

  return (
    <FormContainer>
      <Eyebrow>✦ Mystic Tarot ✦</Eyebrow>
      <Title>{t('questionForm.title')}</Title>
      <Subtitle>
        {t('questionForm.placeholder')}
      </Subtitle>
      <Form onSubmit={handleSubmit}>
        <TextArea
          placeholder={t('questionForm.placeholder')}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
        />
        <Button type="submit">{t('questionForm.submit')}</Button>
      </Form>
    </FormContainer>
  );
};

export default QuestionForm;
